import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bars3Icon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { StickyBackBar } from '@/components/layout/StickyBackBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { IconButton } from '@/components/ui/IconButton';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { TagForm } from '@/components/tags/TagForm';
import { CollectionForm } from '@/components/collections/CollectionForm';
import { DeleteTagDialog } from '@/components/tags/DeleteTagDialog';
import { DeleteCollectionDialog } from '@/components/collections/DeleteCollectionDialog';
import { useAttributionBatch } from '@/api/hooks/useAttributionBatch';
import {
    useCreateTag,
    useDeleteTag,
    useSetTagLedgers,
    useUpdateTag,
} from '@/api/hooks/useTags';
import {
    useCreateCollection,
    useDeleteCollection,
    useMoveLedgersToCollection,
    useReorderCollection,
    useUpdateCollection,
} from '@/api/hooks/useCollections';
import { useAuth } from '@/contexts/AuthContext';
import { getErrorMessage } from '@/utils/errorHandler';
import { isLedgerArchived } from '@/utils/ledger';
import { COLOR_DOT_CLASSES } from '@/utils/color';
import { usePageTitle } from '@/hooks/usePageTitle';
import { TOGGLE_STYLES, TOGGLE_BUTTON_BASE } from '@/styles/toggleStyles';
import type {
    Ledger,
    LedgerType,
    Color,
    CreateTagData,
    CreateCollectionData,
    UpdateTagData,
    UpdateCollectionData,
} from '@/api/types';

type AttributionKind = 'collection' | 'tag';

interface Selection {
    kind: AttributionKind;
    id: number;
}

const MOVE_MEMBER_TOOLTIP =
    'A ledger always belongs to a collection — move it by selecting it from its destination collection.';

interface AttributionRow {
    id: number;
    label: string;
    color: Color;
    count: number;
}

interface AttributionRowButtonProps {
    kind: AttributionKind;
    item: AttributionRow;
    isActive: boolean;
    onSelect: (selection: Selection) => void;
}

/** Selectable row inside an attribution group. */
function AttributionRowButton({ kind, item, isActive, onSelect }: AttributionRowButtonProps) {
    return (
        <button
            type="button"
            onClick={() => onSelect({ kind, id: item.id })}
            className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors ${
                isActive
                    ? 'bg-active-light dark:bg-active-dark'
                    : 'hover:bg-gray-50 dark:hover:bg-dark'
            }`}
            aria-pressed={isActive}
        >
            <span
                className={`h-2.5 w-2.5 shrink-0 rounded-full ${COLOR_DOT_CLASSES[item.color]}`}
                aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
                {item.label}
            </span>
            <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">{item.count}</span>
        </button>
    );
}

/**
 * Drag-sortable wrapper around AttributionRowButton with a desktop-only
 * drag handle (mirrors SortableCollectionRow).
 */
function SortableAttributionRow(props: AttributionRowButtonProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.item.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : 'auto',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`relative ${isDragging ? 'shadow-lg' : ''}`}
        >
            <div
                {...attributes}
                {...listeners}
                className="absolute bottom-0 left-0 top-0 z-10 hidden w-8 cursor-grab items-center justify-center transition-colors hover:bg-gray-100 active:cursor-grabbing dark:hover:bg-dark lg:flex"
                title="Drag to reorder"
            >
                <Bars3Icon className="h-4 w-4 text-gray-400" />
            </div>
            <div className="lg:pl-8">
                <AttributionRowButton {...props} />
            </div>
        </div>
    );
}

interface AttributionGroupProps {
    title: string;
    kind: AttributionKind;
    items: AttributionRow[];
    selected: Selection | null;
    onSelect: (selection: Selection) => void;
    onCreate: () => void;
    /** When provided, rows become drag-sortable (collections only — tags have no weight). */
    onReorder?: (id: number, newIndex: number) => void;
}

/**
 * One group ("Collections" or "Tags") in the left attribution panel.
 */
function AttributionGroup({
    title,
    kind,
    items,
    selected,
    onSelect,
    onCreate,
    onReorder,
}: AttributionGroupProps) {
    // dnd-kit sensors — 8px pointer activation keeps plain clicks selecting
    // rows instead of starting a drag (mirrors CollectionList).
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const newIndex = items.findIndex((item) => item.id === over.id);
        if (newIndex === -1) return;
        onReorder?.(Number(active.id), newIndex);
    };

    const rows = items.map((item) => {
        const isActive = selected?.kind === kind && selected.id === item.id;
        return onReorder ? (
            <SortableAttributionRow
                key={item.id}
                kind={kind}
                item={item}
                isActive={isActive}
                onSelect={onSelect}
            />
        ) : (
            <AttributionRowButton
                key={item.id}
                kind={kind}
                item={item}
                isActive={isActive}
                onSelect={onSelect}
            />
        );
    });

    return (
        <section className="overflow-hidden rounded-lg border border-gray-300 bg-white dark:border-gray-700 dark:bg-secondary">
            <div className="flex items-center justify-between border-b border-gray-300 bg-gray-50 px-4 py-2.5 dark:border-gray-700 dark:bg-dark">
                <h2 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                    {title}
                </h2>
                <button
                    type="button"
                    onClick={onCreate}
                    className="flex items-center gap-1 text-sm font-medium text-accent hover:underline"
                    aria-label={`New ${kind}`}
                >
                    <PlusIcon className="h-4 w-4" />
                    New
                </button>
            </div>
            {items.length === 0 ? (
                <p className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                    No {title.toLowerCase()}.
                </p>
            ) : onReorder ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={items.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">{rows}</div>
                    </SortableContext>
                </DndContext>
            ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">{rows}</div>
            )}
        </section>
    );
}

/**
 * Attribution management page: the single place to manage collections and
 * tags — create, edit, delete, reorder collections, and bulk-assign ledgers.
 *
 * Master-detail layout — the left panel lists the user's attributions
 * (collections + tags for the active type), the right panel shows all
 * ledgers of that type with membership checkboxes. Edits are staged and
 * saved with a single bulk API call per attribution.
 */
export function AttributionPage() {
    usePageTitle('Attribution');

    const { refreshProfile } = useAuth();
    const [type, setType] = useState<LedgerType>('asset');
    const { data, isLoading, error } = useAttributionBatch({ type });
    const setTagLedgers = useSetTagLedgers();
    const moveLedgers = useMoveLedgersToCollection();
    const createTag = useCreateTag();
    const createCollection = useCreateCollection();
    const updateTag = useUpdateTag();
    const updateCollection = useUpdateCollection();
    const deleteTag = useDeleteTag();
    const deleteCollection = useDeleteCollection();
    const reorderCollection = useReorderCollection();

    const [selected, setSelected] = useState<Selection | null>(null);
    /** Staged checkbox overrides per ledger id (only stored when differing from the base membership). */
    const [overrides, setOverrides] = useState<Record<number, boolean>>({});
    const [search, setSearch] = useState('');
    const [hideArchived, setHideArchived] = useState(true);
    const [saveError, setSaveError] = useState<string | null>(null);
    /** Action deferred behind the unsaved-changes guard dialog. */
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [createKind, setCreateKind] = useState<AttributionKind | null>(null);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    const ledgers = useMemo(() => data?.ledgers.items ?? [], [data]);
    const collections = useMemo(() => data?.collections.items ?? [], [data]);
    const tags = useMemo(() => data?.tags.items ?? [], [data]);

    // Tag member counts are computed client-side from ledgers' tagIds.
    const tagCounts = useMemo(() => {
        const counts = new Map<number, number>();
        for (const ledger of ledgers) {
            for (const tagId of ledger.tagIds) {
                counts.set(tagId, (counts.get(tagId) ?? 0) + 1);
            }
        }
        return counts;
    }, [ledgers]);

    const collectionsById = useMemo(
        () => new Map(collections.map((c) => [c.id, c])),
        [collections],
    );

    // Weight order — kept in sync by useReorderCollection's optimistic cache
    // update (the attribution batch lives under the ['batch-ledgers'] prefix).
    const collectionRows = useMemo<AttributionRow[]>(
        () =>
            [...collections]
                .sort((a, b) => a.weight - b.weight)
                .map((c) => ({
                    id: c.id,
                    label: c.label,
                    color: c.color,
                    count: c.ledgerCount,
                })),
        [collections],
    );

    const tagRows = useMemo<AttributionRow[]>(
        () =>
            [...tags]
                .sort((a, b) => a.label.localeCompare(b.label))
                .map((t) => ({
                    id: t.id,
                    label: t.label,
                    color: t.color,
                    count: tagCounts.get(t.id) ?? 0,
                })),
        [tags, tagCounts],
    );

    const selectedCollection =
        selected?.kind === 'collection' ? (collectionsById.get(selected.id) ?? null) : null;
    const selectedTag =
        selected?.kind === 'tag' ? (tags.find((t) => t.id === selected.id) ?? null) : null;
    const selectedEntity = selectedCollection ?? selectedTag;

    const isBaseMember = (ledger: Ledger): boolean => {
        if (!selected) return false;
        return selected.kind === 'tag'
            ? ledger.tagIds.includes(selected.id)
            : ledger.collectionId === selected.id;
    };

    const isChecked = (ledger: Ledger): boolean => overrides[ledger.id] ?? isBaseMember(ledger);

    const visibleLedgers = useMemo(() => {
        const term = search.trim().toLowerCase();
        return ledgers
            .filter((l) => !hideArchived || !isLedgerArchived(l))
            .filter((l) => !term || l.label.toLowerCase().includes(term))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [ledgers, hideArchived, search]);

    const additionCount = Object.values(overrides).filter(Boolean).length;
    const removalCount = Object.values(overrides).filter((v) => !v).length;
    const hasChanges = additionCount + removalCount > 0;
    const showFooter = hasChanges && selected !== null;
    const isSaving = setTagLedgers.isPending || moveLedgers.isPending;
    const isMutatingEntity =
        updateTag.isPending ||
        updateCollection.isPending ||
        deleteTag.isPending ||
        deleteCollection.isPending;

    const applySelection = (next: Selection | null) => {
        setSelected(next);
        setOverrides({});
        setSaveError(null);
    };

    /** Selection change gated by the unsaved-changes guard. */
    const requestSelection = (next: Selection | null) => {
        if (hasChanges) {
            setPendingAction(() => () => applySelection(next));
        } else {
            applySelection(next);
        }
    };

    const applyType = (next: LedgerType) => {
        setType(next);
        setSelected(null);
        setOverrides({});
        setSearch('');
        setSaveError(null);
    };

    /** Type switch gated by the unsaved-changes guard. */
    const requestType = (next: LedgerType) => {
        if (next === type) return;
        if (hasChanges) {
            setPendingAction(() => () => applyType(next));
        } else {
            applyType(next);
        }
    };

    const handleToggle = (ledger: Ledger) => {
        const base = isBaseMember(ledger);
        const next = !(overrides[ledger.id] ?? base);
        setOverrides((prev) => {
            const copy = { ...prev };
            if (next === base) {
                delete copy[ledger.id];
            } else {
                copy[ledger.id] = next;
            }
            return copy;
        });
    };

    /** "Select all" / "Clear" helpers acting on the filtered set (tag mode only). */
    const handleBulkToggle = (nextChecked: boolean) => {
        if (selected?.kind !== 'tag') return;
        const tagId = selected.id;
        setOverrides((prev) => {
            const copy = { ...prev };
            for (const ledger of visibleLedgers) {
                const base = ledger.tagIds.includes(tagId);
                if (nextChecked === base) {
                    delete copy[ledger.id];
                } else {
                    copy[ledger.id] = nextChecked;
                }
            }
            return copy;
        });
    };

    const handleDiscard = () => {
        setOverrides({});
        setSaveError(null);
    };

    /** Saves the staged diff with exactly one bulk API call. Returns true on success. */
    const handleSave = async (): Promise<boolean> => {
        if (!selected || !hasChanges) return true;
        setSaveError(null);
        try {
            if (selected.kind === 'tag') {
                // Full desired member set (replacement semantics).
                const ledgerIds = ledgers
                    .filter((l) => isChecked(l))
                    .map((l) => l.id)
                    .sort((a, b) => a - b);
                await setTagLedgers.mutateAsync({ id: selected.id, data: { ledgerIds } });
            } else {
                // Only the staged additions (additive-move semantics).
                const ledgerIds = Object.entries(overrides)
                    .filter(([, checked]) => checked)
                    .map(([id]) => Number(id))
                    .sort((a, b) => a - b);
                await moveLedgers.mutateAsync({ id: selected.id, data: { ledgerIds } });
            }
            setOverrides({});
            toast.success('Attribution saved');
            return true;
        } catch (err) {
            setSaveError(getErrorMessage(err));
            return false;
        }
    };

    const handleGuardSave = async () => {
        const action = pendingAction;
        const ok = await handleSave();
        setPendingAction(null);
        if (ok) action?.();
    };

    const handleGuardDiscard = () => {
        const action = pendingAction;
        setOverrides({});
        setSaveError(null);
        setPendingAction(null);
        action?.();
    };

    // Inline create: auto-select the new attribution so the user can assign
    // members immediately (the "introduce a new tag" workflow).
    const handleCreateTag = async (formData: CreateTagData) => {
        const tag = await createTag.mutateAsync({ ...formData, type });
        setCreateKind(null);
        requestSelection({ kind: 'tag', id: tag.id });
    };

    const handleCreateCollection = async (formData: CreateCollectionData) => {
        const collection = await createCollection.mutateAsync({ ...formData, type });
        setCreateKind(null);
        requestSelection({ kind: 'collection', id: collection.id });
    };

    const handleUpdateTag = async (formData: UpdateTagData) => {
        if (!selectedTag) return;
        await updateTag.mutateAsync({ id: selectedTag.id, data: formData });
        setIsEditOpen(false);
        toast.success('Tag updated');
    };

    const handleUpdateCollection = async (formData: UpdateCollectionData) => {
        if (!selectedCollection) return;
        await updateCollection.mutateAsync({ id: selectedCollection.id, data: formData });
        setIsEditOpen(false);
        toast.success('Collection updated');
    };

    // Deleting discards any staged changes with the entity — the delete
    // dialog itself is the confirmation, no extra unsaved-changes prompt.
    const handleDeleteTag = async (id: number) => {
        await deleteTag.mutateAsync(id);
        setIsDeleteOpen(false);
        applySelection(null);
        toast.success('Tag deleted');
    };

    const handleDeleteCollection = async (id: number) => {
        await deleteCollection.mutateAsync(id);
        // Refresh profile to sync onboarding state (hasAssetCollections/hasDebtCollections).
        await refreshProfile();
        setIsDeleteOpen(false);
        applySelection(null);
        toast.success('Collection deleted');
    };

    const handleReorderCollection = (id: number, newIndex: number) => {
        reorderCollection.mutate({ id, weight: newIndex });
    };

    const mobileOptions = useMemo(
        () => [
            ...collectionRows.map((c) => ({
                value: `collection:${c.id}`,
                label: `Collection · ${c.label} (${c.count})`,
            })),
            ...tagRows.map((t) => ({
                value: `tag:${t.id}`,
                label: `Tag · ${t.label} (${t.count})`,
            })),
        ],
        [collectionRows, tagRows],
    );

    const handleMobileSelect = (value: string) => {
        const [kind, rawId] = value.split(':');
        const id = Number(rawId);
        if ((kind === 'collection' || kind === 'tag') && Number.isFinite(id)) {
            requestSelection({ kind, id });
        }
    };

    return (
        <>
            <StickyBackBar destinationLabel="Settings" backPath="/settings" />
            {/* Reserve space at the bottom while the fixed footer is visible so
                the last checklist rows are never hidden behind it. */}
            <div className={`space-y-6 pt-4 ${showFooter ? 'pb-24' : 'pb-4'}`}>
                <PageHeader title="Attribution" />

                {/* Type tabs: Assets | Debts */}
                <div className={TOGGLE_STYLES.container} role="group" aria-label="Ledger type">
                    {(['asset', 'debt'] as const).map((t) => (
                        <button
                            key={t}
                            type="button"
                            onClick={() => requestType(t)}
                            className={`h-8 px-4 text-sm font-medium ${TOGGLE_BUTTON_BASE} ${
                                type === t ? TOGGLE_STYLES.active : TOGGLE_STYLES.inactive
                            }`}
                            aria-pressed={type === t}
                        >
                            {t === 'asset' ? 'Assets' : 'Debts'}
                        </button>
                    ))}
                </div>

                {isLoading && (
                    <div className="flex justify-center py-12">
                        <div className="text-gray-500 dark:text-gray-400">
                            Loading attribution data...
                        </div>
                    </div>
                )}

                {error && <ErrorAlert message={getErrorMessage(error)} />}

                {!isLoading && !error && (
                    <div className="space-y-6 md:grid md:grid-cols-3 md:items-start md:gap-6 md:space-y-0">
                        {/* Left panel: attribution list */}
                        <div className="md:col-span-1">
                            {/* Mobile: collapsed to a Select above the checklist */}
                            <div className="space-y-3 md:hidden">
                                <Select
                                    label="Attribution"
                                    value={selected ? `${selected.kind}:${selected.id}` : ''}
                                    onChange={handleMobileSelect}
                                    options={mobileOptions}
                                />
                                <div className="flex gap-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setCreateKind('collection')}
                                    >
                                        <PlusIcon className="mr-1 h-4 w-4" />
                                        Collection
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setCreateKind('tag')}
                                    >
                                        <PlusIcon className="mr-1 h-4 w-4" />
                                        Tag
                                    </Button>
                                </div>
                            </div>

                            {/* Desktop: grouped list */}
                            <div className="hidden space-y-6 md:block">
                                <AttributionGroup
                                    title="Collections"
                                    kind="collection"
                                    items={collectionRows}
                                    selected={selected}
                                    onSelect={requestSelection}
                                    onCreate={() => setCreateKind('collection')}
                                    onReorder={handleReorderCollection}
                                />
                                <AttributionGroup
                                    title="Tags"
                                    kind="tag"
                                    items={tagRows}
                                    selected={selected}
                                    onSelect={requestSelection}
                                    onCreate={() => setCreateKind('tag')}
                                />
                            </div>
                        </div>

                        {/* Right panel: member checklist */}
                        <div className="md:col-span-2">
                            {!selected && (
                                <div className="rounded-lg border border-gray-300 bg-white p-8 text-center dark:border-gray-700 dark:bg-secondary">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        Select a collection or tag to manage its members.
                                    </p>
                                </div>
                            )}

                            {selected && !selectedEntity && (
                                <div className="rounded-lg border border-gray-300 bg-white p-8 text-center dark:border-gray-700 dark:bg-secondary">
                                    <p className="text-gray-500 dark:text-gray-400">Loading...</p>
                                </div>
                            )}

                            {selected && selectedEntity && (
                                <section className="rounded-lg border border-gray-300 bg-white dark:border-gray-700 dark:bg-secondary">
                                    <div className="space-y-3 border-b border-gray-300 px-4 py-3 dark:border-gray-700">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <div className="flex min-w-0 items-center gap-2">
                                                <span
                                                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${COLOR_DOT_CLASSES[selectedEntity.color]}`}
                                                    aria-hidden="true"
                                                />
                                                <h2 className="truncate font-semibold text-gray-900 dark:text-white">
                                                    {selectedEntity.label}
                                                </h2>
                                                <span className="shrink-0 rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                                    {selected.kind}
                                                </span>
                                            </div>
                                            {/* Actions for the selected attribution (desktop + mobile) */}
                                            <div className="flex shrink-0 items-center gap-2">
                                                <Link
                                                    to={`/${type}s?${selected.kind === 'tag' ? 'tags' : 'collections'}=${selected.id}`}
                                                    className="mr-1 text-sm font-medium text-accent hover:underline"
                                                >
                                                    View {type === 'asset' ? 'assets' : 'debts'}
                                                </Link>
                                                <IconButton
                                                    size="sm"
                                                    variant="default"
                                                    onClick={() => setIsEditOpen(true)}
                                                    aria-label={`Edit ${selected.kind}`}
                                                    disabled={isMutatingEntity}
                                                >
                                                    <PencilIcon className="h-4 w-4" />
                                                </IconButton>
                                                <IconButton
                                                    size="sm"
                                                    variant="danger-muted"
                                                    onClick={() => setIsDeleteOpen(true)}
                                                    aria-label={`Delete ${selected.kind}`}
                                                    disabled={isMutatingEntity}
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </IconButton>
                                            </div>
                                        </div>
                                        {selectedEntity.description && (
                                            <p className="whitespace-pre-wrap text-sm text-gray-500 dark:text-gray-400">
                                                {selectedEntity.description}
                                            </p>
                                        )}
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                            <Input
                                                placeholder="Search ledgers..."
                                                aria-label="Search ledgers"
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                            />
                                            <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                                <Checkbox
                                                    checked={hideArchived}
                                                    onChange={(e) => setHideArchived(e.target.checked)}
                                                />
                                                Hide archived
                                            </label>
                                            {selected.kind === 'tag' && (
                                                <div className="flex shrink-0 gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleBulkToggle(true)}
                                                        className="text-sm font-medium text-accent hover:underline"
                                                    >
                                                        Select all
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleBulkToggle(false)}
                                                        className="text-sm font-medium text-gray-500 hover:underline dark:text-gray-400"
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {selected.kind === 'collection' && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                Current members can&apos;t be unchecked — a ledger
                                                always belongs to a collection. To move one out,
                                                select it from its destination collection.
                                            </p>
                                        )}
                                    </div>

                                    {saveError && (
                                        <div className="px-4 pt-3">
                                            <ErrorAlert
                                                message={saveError}
                                                onDismiss={() => setSaveError(null)}
                                            />
                                        </div>
                                    )}

                                    {visibleLedgers.length === 0 ? (
                                        <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No matching {type === 'asset' ? 'assets' : 'debts'}.
                                        </p>
                                    ) : (
                                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                            {visibleLedgers.map((ledger) => {
                                                const base = isBaseMember(ledger);
                                                const checked = isChecked(ledger);
                                                const isLockedMember =
                                                    selected.kind === 'collection' && base;
                                                const currentCollection = collectionsById.get(
                                                    ledger.collectionId,
                                                );
                                                return (
                                                    <label
                                                        key={ledger.id}
                                                        title={isLockedMember ? MOVE_MEMBER_TOOLTIP : undefined}
                                                        className={`flex items-center gap-3 px-4 py-2.5 ${
                                                            isLockedMember
                                                                ? 'cursor-not-allowed'
                                                                : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dark'
                                                        }`}
                                                    >
                                                        <Checkbox
                                                            checked={checked}
                                                            disabled={isLockedMember || isSaving}
                                                            onChange={() => handleToggle(ledger)}
                                                        />
                                                        <span className="min-w-0 flex-1">
                                                            <span className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                                                                <span className="truncate">
                                                                    {ledger.label}
                                                                </span>
                                                                {isLedgerArchived(ledger) && (
                                                                    <span className="shrink-0 rounded-sm bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                                                        Archived
                                                                    </span>
                                                                )}
                                                            </span>
                                                            {selected.kind === 'collection' &&
                                                                !base &&
                                                                currentCollection && (
                                                                    <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                                                        {checked
                                                                            ? `from ${currentCollection.label}`
                                                                            : currentCollection.label}
                                                                    </span>
                                                                )}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </section>
                            )}
                        </div>
                    </div>
                )}

                {/* Fixed footer with the pending diff — pinned to the viewport
                    bottom; at lg+ offset by the live sidebar width exposed as
                    the --sidebar-w CSS variable on the Layout root. Stays at
                    z-30, below MobileDrawer (z-50) and sonner toasts. */}
                {showFooter && (
                    <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-300 bg-white transition-[left] duration-300 dark:border-gray-700 dark:bg-darker lg:left-(--sidebar-w,0px)">
                        <div className="flex max-w-(--breakpoint-2xl) items-center justify-between gap-4 px-4 py-3 md:px-6 lg:px-8">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                <span className="font-semibold">Pending:</span>{' '}
                                <span className="font-medium text-green-600 dark:text-green-400">
                                    +{additionCount}
                                </span>
                                {' / '}
                                <span className="font-medium text-red-600 dark:text-red-400">
                                    −{removalCount}
                                </span>
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleDiscard}
                                    disabled={isSaving}
                                >
                                    Discard
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => void handleSave()}
                                    isLoading={isSaving}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Unsaved-changes guard */}
                <Dialog
                    open={pendingAction !== null}
                    onClose={() => setPendingAction(null)}
                    title="Unsaved changes"
                    size="sm"
                >
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                        You have unsaved attribution changes (+{additionCount} / −{removalCount}).
                        Save them before leaving?
                    </p>
                    <div className="mt-6 flex justify-end gap-3">
                        <Button variant="ghost" onClick={() => setPendingAction(null)} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button variant="secondary" onClick={handleGuardDiscard} disabled={isSaving}>
                            Discard
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => void handleGuardSave()}
                            isLoading={isSaving}
                        >
                            Save
                        </Button>
                    </div>
                </Dialog>

                {/* Inline create dialogs (existing forms) */}
                <Dialog
                    open={createKind === 'tag'}
                    onClose={() => setCreateKind(null)}
                    title={`Create ${type === 'asset' ? 'Asset' : 'Debt'} Tag`}
                >
                    <TagForm
                        onSubmit={handleCreateTag}
                        onCancel={() => setCreateKind(null)}
                        isLoading={createTag.isPending}
                    />
                </Dialog>

                <Dialog
                    open={createKind === 'collection'}
                    onClose={() => setCreateKind(null)}
                    title={`Create ${type === 'asset' ? 'Asset' : 'Debt'} Collection`}
                >
                    <CollectionForm
                        onSubmit={handleCreateCollection}
                        onCancel={() => setCreateKind(null)}
                        isLoading={createCollection.isPending}
                    />
                </Dialog>

                {/* Edit dialog for the selected attribution (existing forms in edit mode) */}
                <Dialog
                    open={isEditOpen}
                    onClose={() => setIsEditOpen(false)}
                    title={selected?.kind === 'collection' ? 'Edit Collection' : 'Edit Tag'}
                >
                    {selectedTag && (
                        <TagForm
                            tag={selectedTag}
                            onSubmit={handleUpdateTag}
                            onCancel={() => setIsEditOpen(false)}
                            isLoading={updateTag.isPending}
                        />
                    )}
                    {selectedCollection && (
                        <CollectionForm
                            collection={selectedCollection}
                            onSubmit={handleUpdateCollection}
                            onCancel={() => setIsEditOpen(false)}
                            isLoading={updateCollection.isPending}
                        />
                    )}
                </Dialog>

                {/* Delete confirmations (reused dialogs, incl. the collection ledger-count guard) */}
                <DeleteTagDialog
                    tag={selectedTag}
                    open={isDeleteOpen && selected?.kind === 'tag'}
                    onClose={() => setIsDeleteOpen(false)}
                    onConfirm={handleDeleteTag}
                    isLoading={deleteTag.isPending}
                />

                <DeleteCollectionDialog
                    collection={selectedCollection}
                    open={isDeleteOpen && selected?.kind === 'collection'}
                    onClose={() => setIsDeleteOpen(false)}
                    onConfirm={handleDeleteCollection}
                    isLoading={deleteCollection.isPending}
                />
            </div>
        </>
    );
}
